import { motion } from "framer-motion";

// ---------------------------------------------------------------------------
// EXECUTIVE SYSTEM — Phase 1 hero/system MOCK (unlinked preview route)
// Light, gallery-grade. Jost (geometric sans, light) + Cormorant Garamond
// (serif accent). The & is always Cormorant italic copper. One ink anchor
// section sitewide; everything else paper. Minimal motion: slow reveals only.
// Real Home copy is used so the read is realistic.
// ---------------------------------------------------------------------------

const PARTNERS = ["Salesforce", "Apple", "Howard University", "American Bar Association", "The White House"];

const CASES = [
  {
    sector: "Digital health",
    title: "An MVP without giving up the company.",
    problem: "Ambitious AI health concept, no developer-ready scope, no path that did not cost equity.",
    caliber: "CTO-grade product architect, scoped to what mattered.",
    outcome: "Developer-ready MVP with a non-dilutive growth path. Founder kept 100% equity.",
  },
  {
    sector: "Enterprise",
    title: "From technical ambiguity to funder-ready.",
    problem: "Conflated product workstreams and gaps that would fail diligence.",
    caliber: "Senior architects plus a governance lead.",
    outcome: "Nine gaps surfaced and a documented readiness picture leadership could show funders.",
  },
  {
    sector: "Government",
    title: "AI readiness that holds under scrutiny.",
    problem: "AI ambition running ahead of governance and infrastructure documentation.",
    caliber: "Senior architects and a governance lead.",
    outcome: "Readiness and governance work built to survive board and regulatory review.",
  },
];

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const Wordmark = ({ className = "" }) => (
  <span className={`font-light tracking-[0.22em] ${className}`}>
    COPPER <span className="exec-amp">&amp;</span> VINE
  </span>
);

const Eyebrow = ({ children }) => (
  <p className="font-normal text-[11px] uppercase tracking-[0.3em] text-[color:var(--copper-deep)]">{children}</p>
);

export default function ExecutivePreview() {
  return (
    <div className="exec-scope min-h-screen" data-testid="exec-preview">
      <div className="mx-auto max-w-[1180px] px-6 md:px-16">
        {/* HEADER — the fine rule motif above the wordmark */}
        <header className="pt-10" data-testid="exec-header">
          <div className="h-px w-full bg-[color:var(--ink)] mb-6" />
          <div className="flex items-baseline justify-between">
            <Wordmark className="text-[22px]" />
            <nav className="hidden md:flex items-center gap-10 text-[11.5px] uppercase tracking-[0.2em] font-normal">
              <a href="#work" className="text-[color:var(--grey)] hover:text-[color:var(--copper)] transition-colors duration-300" data-testid="nav-work">Work</a>
              <a href="#" className="text-[color:var(--grey)] hover:text-[color:var(--copper)] transition-colors duration-300">Services</a>
              <a href="#" className="text-[color:var(--grey)] hover:text-[color:var(--copper)] transition-colors duration-300">About</a>
              <a href="#" className="text-[color:var(--copper-deep)] hover:opacity-70 transition-opacity duration-300" data-testid="nav-cta">Start a Sprint</a>
            </nav>
          </div>
        </header>

        {/* HERO */}
        <motion.section {...fade} className="pt-28 md:pt-32 pb-16 max-w-[920px]" data-testid="home-hero">
          <div className="mb-10"><Eyebrow>Executive Technology Advisory &nbsp;·&nbsp; Since 2003</Eyebrow></div>
          <h1
            className="font-extralight text-[clamp(40px,5.6vw,76px)] leading-[1.08] tracking-[0.005em]"
            data-testid="home-hero-headline"
          >
            Build, raise, and scale with{" "}
            <span className="exec-serif italic font-medium text-[color:var(--copper)] tracking-normal">confidence.</span>
          </h1>
          <p className="exec-serif font-normal text-[clamp(20px,2.2vw,25px)] leading-[1.5] text-[color:var(--lead-ink)] max-w-[46ch] mt-9">
            We are the senior team brought in when the decision is expensive and the call is yours. We
            build the systems you run on, and leave your team more capable, documented, and independent
            than we found it.
          </p>
          <div className="flex flex-wrap gap-4 mt-12">
            <a
              href="#"
              data-testid="home-hero-primary-cta"
              className="font-normal text-[12px] uppercase tracking-[0.18em] px-[30px] py-4 border border-transparent bg-[color:var(--ink)] text-[color:var(--paper)] hover:bg-[color:var(--copper)] transition-colors duration-300 rounded-[2px]"
            >
              Start a Sprint
            </a>
            <a
              href="#"
              data-testid="home-hero-secondary-cta"
              className="font-normal text-[12px] uppercase tracking-[0.18em] px-[30px] py-4 border border-[color:var(--ink)] text-[color:var(--ink)] hover:border-[color:var(--copper)] hover:text-[color:var(--copper)] transition-colors duration-300 rounded-[2px]"
            >
              Request the brief
            </a>
          </div>
        </motion.section>

        {/* PROOF — understated tracked partners row */}
        <motion.section {...fade} className="mt-16 pt-7 border-t border-[color:var(--rule)] max-w-[920px]" data-testid="proof-band">
          <p className="text-[10.5px] uppercase tracking-[0.28em] text-[color:var(--grey)] font-normal mb-4">Selected Partners</p>
          <div className="flex flex-wrap gap-x-9 gap-y-2 text-[13px] tracking-[0.16em] font-light">
            {PARTNERS.map((p, i) => (
              <span key={p} className="relative">
                {p}
                {i < PARTNERS.length - 1 && <span className="absolute -right-[18px] text-[color:var(--copper)]">·</span>}
              </span>
            ))}
          </div>
        </motion.section>
      </div>

      {/* WORK — light hairline-bordered cards */}
      <section id="work" className="mt-32 py-4" data-testid="work-section">
        <div className="mx-auto max-w-[1180px] px-6 md:px-16">
          <div className="h-px w-full bg-[color:var(--rule)] mb-16" />
          <motion.div {...fade} className="max-w-[760px]">
            <Eyebrow>01 &nbsp;·&nbsp; Selected Work</Eyebrow>
            <h2 className="font-light text-[clamp(32px,4vw,44px)] leading-[1.12] mt-5">
              Senior work, on the decisions that don’t get a second take.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px mt-14 bg-[color:var(--rule)] p-px">
            {CASES.map((c) => (
              <motion.article
                {...fade}
                key={c.sector}
                className="bg-[color:var(--paper)] p-8 group"
                data-testid={`work-card-${c.sector.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="text-[10.5px] uppercase tracking-[0.28em] text-[color:var(--copper-deep)] font-normal">{c.sector}</p>
                <h3 className="font-normal text-[22px] leading-[1.25] mt-5">{c.title}</h3>
                <dl className="mt-7 space-y-4">
                  {[["Problem", c.problem], ["Caliber", c.caliber], ["Outcome", c.outcome]].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[10px] uppercase tracking-[0.26em] text-[color:var(--copper-deep)] font-normal">{k}</dt>
                      <dd className="exec-serif text-[16.5px] leading-[1.5] text-[color:var(--lead-ink)] mt-1">{v}</dd>
                    </div>
                  ))}
                </dl>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* THE ONE INK ANCHOR SECTION (final CTA) — paper text, inverted buttons */}
      <section
        className="exec-ink-section mt-32 bg-[color:var(--ink)] text-[color:var(--paper)] py-28"
        data-testid="ink-anchor-section"
      >
        <div className="mx-auto max-w-[1180px] px-6 md:px-16">
          <motion.div {...fade} className="max-w-[820px]">
            <p className="font-normal text-[11px] uppercase tracking-[0.3em] text-[color:var(--copper)]">The next step</p>
            <h2 className="font-extralight text-[clamp(34px,4.6vw,58px)] leading-[1.1] mt-7">
              The decision is expensive. The call is{" "}
              <span className="exec-serif italic font-medium text-[color:var(--copper)]">yours.</span>
            </h2>
            <p className="exec-serif font-normal text-[clamp(19px,2vw,23px)] leading-[1.5] mt-7 max-w-[48ch] text-[color:rgba(250,249,246,0.9)]">
              Start where the pain is. A short, senior engagement that leaves you with a decision you can
              defend, and a team more capable than we found it.
            </p>
            <div className="flex flex-wrap gap-4 mt-11">
              <a
                href="#"
                data-testid="ink-primary-cta"
                className="font-normal text-[12px] uppercase tracking-[0.18em] px-[30px] py-4 bg-[color:var(--paper)] text-[color:var(--ink)] hover:bg-[color:var(--copper)] hover:text-[color:var(--paper)] transition-colors duration-300 rounded-[2px]"
              >
                Start a Sprint
              </a>
              <a
                href="#"
                className="font-normal text-[12px] uppercase tracking-[0.18em] px-[30px] py-4 border border-[color:rgba(250,249,246,0.45)] text-[color:var(--paper)] hover:border-[color:var(--copper)] hover:text-[color:var(--copper)] transition-colors duration-300 rounded-[2px]"
              >
                Take the Scorecard
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER — light, rule motif, refined */}
      <footer className="py-14" data-testid="exec-footer">
        <div className="mx-auto max-w-[1180px] px-6 md:px-16">
          <div className="h-px w-full bg-[color:var(--rule)] mb-8" />
          <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-6">
            <Wordmark className="text-[18px]" />
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-[color:var(--grey)] font-normal">
              <a href="#" className="hover:text-[color:var(--copper)] transition-colors duration-300">Work</a>
              <a href="#" className="hover:text-[color:var(--copper)] transition-colors duration-300">Services</a>
              <a href="#" className="hover:text-[color:var(--copper)] transition-colors duration-300">About</a>
              <a href="#" className="hover:text-[color:var(--copper)] transition-colors duration-300">Resources</a>
            </div>
          </div>
          <p className="text-[10.5px] tracking-[0.18em] text-[color:var(--grey)] font-light mt-8">
            © 2026 COPPER &amp; VINE &nbsp;·&nbsp; EXECUTIVE TECHNOLOGY ADVISORY SINCE 2003
          </p>
        </div>
      </footer>
    </div>
  );
}
